/**
 * The floating material's recipe, in one place — but the VALUES are not here.
 *
 * This file's header used to promise that the glass "must not be re-derived per
 * surface or the panels drift apart within a week", and then hard-coded three
 * different fills a few lines below it, while `.panel` in global.css quietly
 * carried a fourth. The promise was right and nothing enforced it. So the fill,
 * edge and drop now live as custom properties in `tokens.css`, and everything
 * here — like `.panel` over in the stylesheet — reads the SAME
 * `var(--glass-fill)` string rather than restating its value. Neither layer owns
 * the number; both point at it.
 *
 * That means these recipes contain no colour at all. That is deliberate and it
 * is tested: `glass.test.ts` fails the build on any colour literal in this file,
 * because a literal here is precisely how the drift started. Surfaces may differ
 * in SHAPE (radius, how far off the field they float). They may not differ in
 * MATERIAL.
 */

import type { CSSProperties } from 'react'

/**
 * The one material for anything that floats over the field: the design's card,
 * opaque, inside a hairline edge (D358). The name is the old one. It was a
 * liquid glass — a lens at the rim, a white sheen, an inner shade on a pane, a
 * heavy frost — until D357 took those off, and a translucent fill over a light
 * blur until D358 made it a card the graph does not show through.
 *
 * `lift` is the one thing a surface still chooses. A `card` — the field card, a
 * popover, the composer afloat, a pane on a phone — takes the design's one soft
 * drop. A `flat` piece of chrome — the tool dock, a bar, a button on the field —
 * takes none, as the design draws its dock, its replay bar and its coach button.
 *
 * No `theme` argument, and that is load-bearing rather than tidy: the custom
 * properties already resolve per theme at the root, so a recipe that branched
 * on `theme` would be a second place the two themes could disagree — and it
 * would have to name colours to do it, which is the thing this file may not do.
 * Dropping the parameter is what makes "no literal here" reachable at all.
 */
export function glass(lift: 'card' | 'flat' = 'card'): CSSProperties {
  return {
    background: 'var(--glass-fill)',
    border: '1px solid var(--glass-edge)',
    boxShadow: lift === 'card' ? 'var(--glass-drop)' : 'none',
  }
}
